/**
 * DeckManager - Handles deck creation, shuffling, and dealing for the Golf card game.
 *
 * Uses Fisher-Yates shuffle with crypto.getRandomValues() for cryptographically
 * secure randomness, ensuring fair and unpredictable card distribution.
 */

import { Card, Suit, Rank } from './types';

const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

/**
 * Creates a standard 52-card deck, or a double deck (104 cards) for 3-4 player games.
 *
 * @param useDoubleDeck - If true, returns 104 cards (two copies of the standard deck)
 * @returns An array of Card objects
 */
export function createDeck(useDoubleDeck: boolean): Card[] {
  const deck: Card[] = [];

  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank });
    }
  }

  if (useDoubleDeck) {
    // Duplicate all 52 cards to get 104
    const copy = deck.map(card => ({ ...card }));
    deck.push(...copy);
  }

  return deck;
}

/**
 * Shuffles a deck of cards in-place using the Fisher-Yates algorithm
 * with crypto.getRandomValues() for cryptographically secure randomness.
 *
 * Returns a new shuffled array (does not mutate the input).
 *
 * @param deck - The deck of cards to shuffle
 * @returns A new array containing the same cards in shuffled order
 */
export function shuffle(deck: Card[]): Card[] {
  const shuffled = [...deck];
  const length = shuffled.length;

  if (length <= 1) {
    return shuffled;
  }

  // Generate all random values at once for efficiency
  const randomValues = new Uint32Array(length - 1);
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.getRandomValues) {
    globalThis.crypto.getRandomValues(randomValues);
  } else {
    // Fallback for environments without Web Crypto API
    for (let k = 0; k < randomValues.length; k++) {
      randomValues[k] = Math.floor(Math.random() * 0xFFFFFFFF);
    }
  }

  // Fisher-Yates shuffle (from end to beginning)
  for (let i = length - 1; i > 0; i--) {
    // Generate a random index j where 0 <= j <= i
    const j = randomValues[i - 1] % (i + 1);
    // Swap elements at indices i and j
    const temp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = temp;
  }

  return shuffled;
}

/**
 * Deals a specified number of cards from the top of the deck.
 *
 * @param deck - The deck to deal from
 * @param count - The number of cards to deal
 * @returns An object with `dealt` (the dealt cards) and `remaining` (the rest of the deck)
 * @throws Error if count exceeds the number of available cards
 */
export function deal(deck: Card[], count: number): { dealt: Card[]; remaining: Card[] } {
  if (count < 0) {
    throw new Error(`Cannot deal a negative number of cards: ${count}`);
  }
  if (count > deck.length) {
    throw new Error(`Cannot deal ${count} cards from a deck of ${deck.length}`);
  }

  const dealt = deck.slice(0, count);
  const remaining = deck.slice(count);

  return { dealt, remaining };
}
